import {
  Component,
  Input,
  Output,
  EventEmitter,
  ContentChild,
  ElementRef,
  AfterContentInit,
  HostBinding,
} from '@angular/core';
import { CommonModule } from '@angular/common';

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'outline'
  | 'danger'
  | 'ghost'
  | 'warning';
export type ButtonSize = 'small' | 'medium' | 'large' | 'lg' | 'input' | 'icon';

@Component({
  selector: 'app-button',
  standalone: true,
  imports: [CommonModule],
  template: `
    <button
      [type]="type"
      [attr.form]="form || null"
      [disabled]="disabled || loading"
      [class]="getButtonClasses()"
      (click)="onClick($event)"
    >
      @if (loading) {
        <div class="spinner"></div>
      }
      <span class="button-text">
        <ng-content></ng-content>
        @if (!hasContent) {
          {{ text }}
        }
      </span>
    </button>
  `,
  styles: [
    `
      :host {
        display: inline-block;
        vertical-align: middle;
        max-width: 100%;
      }

      :host(.button-host-full-width) {
        display: block;
        width: 100%;
      }

      button {
        display: inline-flex;
        flex-direction: row;
        justify-content: center;
        align-items: center;
        gap: 8px;
        border: 1px solid transparent;
        border-radius: var(--radius-md, 10px);
        font-family: var(--font-main);
        font-weight: 600;
        font-size: 14px;
        cursor: pointer;
        transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease;
        box-sizing: border-box;
        position: relative;
        overflow: hidden;
        /* Кнопка заполняет хост. Пока хост inline-block, 100% равно ширине содержимого,
           а растянутый хост (flex: 1, width: 100% в рядах действий) тянет кнопку за собой.
           Раньше тут был width: auto, и каждому месту с растянутой кнопкой приходилось
           дописывать ::ng-deep на внутренний <button>. */
        width: 100%;
        min-width: 0;
      }

      button.size-icon {
        padding: 0;
        min-width: 40px;
        width: 40px;
        height: 40px;
        border-radius: var(--radius-md);
      }

      button.size-small {
        padding: 0 14px;
        min-height: 32px;
        height: 32px;
        font-size: 13px;
        border-radius: var(--radius-sm, 6px);
        gap: 6px;
      }

      button.size-medium {
        padding: 0 20px;
        min-height: 44px;
        height: 44px;
      }

      button.size-large,
      button.size-lg {
        padding: 0 20px;
        min-height: 44px;
        height: 44px;
      }

      button.size-input {
        min-height: var(--input-min-height);
        padding: 0 var(--input-padding-x);
        font-size: var(--input-font-size);
      }

      /* Primary — green */
      button.variant-primary {
        background: var(--primary);
        color: var(--text-on-accent);
        border-color: var(--primary);
      }
      button.variant-primary:hover:not(:disabled) {
        background: var(--primary-hover);
        border-color: var(--primary-hover);
      }
      button.variant-primary:focus-visible {
        outline: none;
        box-shadow: 0 0 0 2px var(--bg-secondary), 0 0 0 4px var(--primary);
      }

      /* Secondary — elevated gray */
      button.variant-secondary {
        background: var(--bg-elevated);
        color: var(--text-primary);
        border-color: var(--border-default);
      }
      button.variant-secondary:hover:not(:disabled) {
        background: var(--bg-hover);
      }
      button.variant-secondary:focus-visible {
        outline: none;
        box-shadow: 0 0 0 2px var(--border-strong);
      }

      /* Outline */
      button.variant-outline {
        background: var(--bg-card);
        color: var(--text-primary);
        border: 1px solid var(--border-default);
      }
      button.variant-outline:hover:not(:disabled) {
        border-color: var(--border-strong);
        background: var(--bg-hover);
      }
      button.variant-outline:focus-visible {
        outline: none;
        box-shadow: 0 0 0 2px var(--border-strong);
      }

      /* Danger */
      button.variant-danger {
        background: var(--danger);
        color: var(--text-on-accent);
        border-color: var(--danger);
      }
      button.variant-danger:hover:not(:disabled) {
        background: var(--danger-hover);
        border-color: var(--danger-hover);
      }
      button.variant-danger:focus-visible {
        outline: none;
        box-shadow: 0 0 0 2px var(--bg-secondary), 0 0 0 4px var(--danger);
      }

      /* Ghost */
      button.variant-ghost {
        background: transparent;
        color: var(--text-primary);
      }
      button.variant-ghost:hover:not(:disabled) {
        background: var(--bg-hover);
      }
      button.variant-ghost:focus-visible {
        outline: none;
        box-shadow: 0 0 0 2px var(--border-strong);
      }

      /* Warning — orange, legacy */
      button.variant-warning {
        background: var(--status-canceled);
        color: var(--text-on-accent);
        border-color: var(--status-canceled);
      }
      button.variant-warning:hover:not(:disabled) {
        filter: brightness(1.1);
      }
      button.variant-warning:focus-visible {
        outline: none;
        box-shadow: 0 0 0 2px var(--bg-secondary), 0 0 0 4px var(--status-canceled);
      }

      button:disabled {
        background: var(--bg-elevated) !important;
        color: var(--text-muted) !important;
        border-color: transparent !important;
        cursor: not-allowed;
      }

      button.loading {
        cursor: wait;
      }

      .button-text {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        white-space: nowrap;
      }

      .spinner {
        width: 16px;
        height: 16px;
        border: 2px solid transparent;
        border-top: 2px solid currentColor;
        border-radius: 50%;
        animation: spin 1s linear infinite;
      }

      @keyframes spin {
        to { transform: rotate(360deg); }
      }

      button.full-width {
        display: flex;
        width: 100%;
      }

      @media (max-width: 768px) {
        button.size-medium {
          min-height: 44px;
          height: 44px;
        }
      }
    `,
  ],
})
export class ButtonComponent implements AfterContentInit {
  @Input() text = '';
  @Input() variant: ButtonVariant = 'primary';
  @Input() size: ButtonSize = 'medium';
  @Input() type: 'button' | 'submit' | 'reset' = 'button';
  @Input() form: string | null = null;
  @Input() disabled = false;
  @Input() loading = false;
  @Input() fullWidth = false;
  @Output() clicked = new EventEmitter<void>();

  @HostBinding('class.button-host-full-width')
  get hostFullWidth(): boolean {
    return this.fullWidth;
  }

  hasContent = false;

  constructor(private elementRef: ElementRef) {}

  ngAfterContentInit(): void {
    const content = this.elementRef.nativeElement.querySelector('ng-content');
    this.hasContent = !!(content && content.children.length > 0);
  }

  getButtonClasses(): string {
    const classes = [`variant-${this.variant}`, `size-${this.size}`];
    if (this.loading) classes.push('loading');
    if (this.fullWidth) classes.push('full-width');
    return classes.join(' ');
  }

  onClick(event: Event): void {
    if (!this.disabled && !this.loading) this.clicked.emit();
  }
}
